import { useMemo } from 'react';

import { useErpSurveyTargetGroupsQuery } from 'core/graphql';

import { ISurveyTargetGroup, SurveyTriggerKind } from './surveyModel';

interface IUseSurveyTargetGroupsArgs {
  surveyId?: string;
  courseId?: string;
  search?: string;
  onlyActive?: boolean;
  triggerKind: SurveyTriggerKind;
  triggerLessonNumber?: number;
  skip?: boolean;
}

interface ISurveyTargetGroupsHook {
  groups: ISurveyTargetGroup[];
  /** Quick lookup for the multiselect's row renderer. */
  byId: Record<string, ISurveyTargetGroup>;
  loading: boolean;
}

/**
 * Spec 0027 — the constructor's group picker with a per-group PREVIEW of the
 * lesson the trigger resolves to (or the reason the group would be skipped).
 *
 * This is what turns «в группе только 6 занятий» from a post-save skip entry
 * into a greyed-out row with a reason before the manager clicks Save.
 */
export const useSurveyTargetGroups = ({
  surveyId,
  courseId,
  search,
  onlyActive = true,
  triggerKind,
  triggerLessonNumber,
  skip = false,
}: IUseSurveyTargetGroupsArgs): ISurveyTargetGroupsHook => {
  const { data, loading } = useErpSurveyTargetGroupsQuery({
    variables: {
      surveyId,
      courseId,
      search: search || undefined,
      onlyActive,
      triggerKind,
      triggerLessonNumber:
        triggerKind === 'lesson_number' ? triggerLessonNumber : undefined,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Memoized because the `|| []` fallback would otherwise be a fresh array on
  // every render, invalidating the byId memo below (react-hooks/exhaustive-deps
  // is an error in this repo, not a warning).
  const groups = useMemo(() => data?.surveyTargetGroups || [], [
    data?.surveyTargetGroups,
  ]);
  const byId = useMemo(
    () =>
      groups.reduce<Record<string, ISurveyTargetGroup>>((acc, group) => {
        acc[group.groupId] = group;
        return acc;
      }, {}),
    [groups],
  );

  return { groups, byId, loading };
};
